import { Card, CircularProgress, Grid, Icon, IconButton, Switch, InputLabel, Box, Skeleton } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { Form, Formik } from "formik";
import FormField from "layouts/pages/users/new-user/components/FormField";
import PropTypes from "prop-types";
import { useEffect, useState } from 'react';
import { errorAlert, successAlert } from "utils/Functions";
import { get, postNoResponse } from "utils/WebService";
import * as Yup from 'yup';

const validationSchema = Yup.object().shape({
    descricao: Yup.string().required("A descrição é obrigatória"),
    banco: Yup.string().required("O banco é obrigatório").max(10, "Máximo de 10 caracteres"),
    agencia: Yup.string().required("A agência é obrigatória").max(20, "Máximo de 20 caracteres"),
    agenciaDigito: Yup.string().max(2, "Máximo de 2 caracteres"),
    conta: Yup.string().required("A conta é obrigatória").max(20, "Máximo de 20 caracteres"),
    contaDigito: Yup.string().max(2, "Máximo de 2 caracteres"),
    carteira: Yup.string().max(10, "Máximo de 10 caracteres"),
    convenio: Yup.string().max(50, "Máximo de 50 caracteres"),
    codEmpresa: Yup.string().max(50, "Máximo de 50 caracteres"),
    cedente: Yup.string().max(100, "Máximo de 100 caracteres"),
    cnpjCedente: Yup.string().max(20, "Máximo de 20 caracteres"),
    ultimoSequencialRemessa: Yup.number().typeError("Deve ser um número").nullable(),
    ultimoNossoNumero: Yup.number().typeError("Deve ser um número").nullable()
});

const ContaBancariaCreateEdit = ({ contaId, closeCreateEdit }) => {

    const [initialValues, setInitialValues] = useState({
        id: null,
        descricao: "",
        banco: "",
        agencia: "",
        agenciaDigito: "",
        conta: "",
        contaDigito: "",
        carteira: "",
        convenio: "",
        codEmpresa: "",
        cedente: "",
        cnpjCedente: "",
        ativo: true,
        ultimoSequencialRemessa: "",
        ultimoNossoNumero: ""
    });
    const [isLoadingInfo, setIsLoadingInfo] = useState(false);

    useEffect(() => {
        if (contaId) {
            setIsLoadingInfo(true);
            get(`/conta-bancaria/by-id/${contaId}`).then((data) => {
                setInitialValues({
                    id: data.id || null,
                    descricao: data.descricao || "",
                    banco: data.banco || "",
                    agencia: data.agencia || "",
                    agenciaDigito: data.agenciaDigito || "",
                    conta: data.conta || "",
                    contaDigito: data.contaDigito || "",
                    carteira: data.carteira || "",
                    convenio: data.convenio || "",
                    codEmpresa: data.codEmpresa || "",
                    cedente: data.cedente || "",
                    cnpjCedente: data.cnpjCedente || "",
                    ativo: data.ativo !== undefined ? data.ativo : true,
                    ultimoSequencialRemessa: data.ultimoSequencialRemessa || "",
                    ultimoNossoNumero: data.ultimoNossoNumero || ""
                });
                setIsLoadingInfo(false);
            }).catch(() => {
                errorAlert("Erro ao buscar dados da Conta Bancária.");
                setIsLoadingInfo(false);
                closeCreateEdit();
            });
        }
    }, [contaId]);

    const handleSubmit = async (values, actions) => {
        try {
            await postNoResponse("/conta-bancaria/save", values);
            successAlert("Conta Bancária salva com sucesso!");
            closeCreateEdit();
        } catch (error) {
            errorAlert("Erro ao salvar Conta Bancária.");
        } finally {
            actions.setSubmitting(false);
        }
    };

    return (
        <Card id="conta-bancaria-info">
            <Box display="flex" alignItems="center" p={3}>
                <IconButton onClick={closeCreateEdit} sx={{ p: 0, paddingRight: 1, paddingTop: 0.75 }}>
                    <MDTypography variant="h5">
                        <Icon>arrow_back</Icon>
                    </MDTypography>
                </IconButton>
                <MDTypography variant="h6">{contaId ? "Editar" : "Adicionar"} Conta Bancária</MDTypography>
            </Box>

            {isLoadingInfo ? (
                <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                    <CircularProgress color="info" />
                </MDBox>
            ) : (
                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                    enableReinitialize
                >
                    {({ values, errors, touched, setFieldValue, isSubmitting }) => (
                        <Form>
                            <MDBox p={3}>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} sm={6}>
                                        <FormField
                                            type="text"
                                            name="descricao"
                                            label="Descrição"
                                            value={values.descricao}
                                            error={errors.descricao && touched.descricao}
                                            success={values.descricao?.length > 0 && !errors.descricao}
                                            loading={isLoadingInfo}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                        <FormField
                                            type="text"
                                            name="banco"
                                            label="Banco (ex: 341)"
                                            value={values.banco}
                                            error={errors.banco && touched.banco}
                                            success={values.banco?.length > 0 && !errors.banco}
                                            loading={isLoadingInfo}
                                        />
                                    </Grid>
                                    
                                    <Grid item xs={12} sm={3}>
                                        <FormField
                                            type="text"
                                            name="agencia"
                                            label="Agência"
                                            value={values.agencia}
                                            error={errors.agencia && touched.agencia}
                                            success={values.agencia?.length > 0 && !errors.agencia}
                                            loading={isLoadingInfo}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={1}>
                                        <FormField
                                            type="text"
                                            name="agenciaDigito"
                                            label="Díg."
                                            value={values.agenciaDigito}
                                            error={errors.agenciaDigito && touched.agenciaDigito}
                                            loading={isLoadingInfo}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={2} style={{ display: 'flex' }}>
                                        {isLoadingInfo ? <Skeleton width={"80%"} sx={{ mt: 2 }} /> :
                                            <Box display="flex" justifyContent="end" alignItems="center" lineHeight={1}>
                                                <MDTypography variant="button">
                                                    Ativo
                                                </MDTypography>
                                                <Box ml={1}>
                                                    <Switch checked={values.ativo || false}
                                                        onChange={(event) => setFieldValue('ativo', event.target.checked)} />
                                                </Box>
                                            </Box>
                                        }
                                    </Grid>

                                    <Grid item xs={12} sm={3}>
                                        <FormField
                                            type="text"
                                            name="conta"
                                            label="Conta"
                                            value={values.conta}
                                            error={errors.conta && touched.conta}
                                            success={values.conta?.length > 0 && !errors.conta}
                                            loading={isLoadingInfo}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={1}>
                                        <FormField
                                            type="text"
                                            name="contaDigito"
                                            label="Díg."
                                            value={values.contaDigito}
                                            error={errors.contaDigito && touched.contaDigito}
                                            loading={isLoadingInfo}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={3}>
                                        <FormField
                                            type="text"
                                            name="carteira"
                                            label="Carteira"
                                            value={values.carteira}
                                            error={errors.carteira && touched.carteira}
                                            loading={isLoadingInfo}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                        <FormField
                                            type="text"
                                            name="convenio"
                                            label="Convênio"
                                            value={values.convenio}
                                            error={errors.convenio && touched.convenio}
                                            loading={isLoadingInfo}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                        <FormField
                                            type="text"
                                            name="codEmpresa"
                                            label="Código da Empresa (CNAB Header)"
                                            value={values.codEmpresa}
                                            error={errors.codEmpresa && touched.codEmpresa}
                                            loading={isLoadingInfo}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                        <FormField
                                            type="number"
                                            name="ultimoSequencialRemessa"
                                            label="Último Seq. Remessa"
                                            value={values.ultimoSequencialRemessa}
                                            error={errors.ultimoSequencialRemessa && touched.ultimoSequencialRemessa}
                                            loading={isLoadingInfo}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                        <FormField
                                            type="number"
                                            name="ultimoNossoNumero"
                                            label="Último Nosso Número"
                                            value={values.ultimoNossoNumero}
                                            error={errors.ultimoNossoNumero && touched.ultimoNossoNumero}
                                            loading={isLoadingInfo}
                                        />
                                    </Grid>


                                    <Grid item xs={12} sm={6}>
                                        <FormField
                                            type="text"
                                            name="cedente"
                                            label="Nome Cedente (Beneficiário)"
                                            value={values.cedente}
                                            error={errors.cedente && touched.cedente}
                                            loading={isLoadingInfo}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormField
                                            type="text"
                                            name="cnpjCedente"
                                            label="CNPJ Cedente"
                                            value={values.cnpjCedente}
                                            error={errors.cnpjCedente && touched.cnpjCedente}
                                            loading={isLoadingInfo}
                                        />
                                    </Grid>

                                </Grid>

                                <Box display="flex" alignItems="center" justifyContent="end" p={2}>
                                    <MDButton
                                        disabled={isLoadingInfo}
                                        size="large"
                                        variant="contained"
                                        color="info"
                                        circular={false}
                                        iconOnly={false}
                                        type="submit"
                                        endIcon={isSubmitting ?
                                            <CircularProgress
                                                color="inherit"
                                                size={20} sx={{ ml: 0, mr: 0 }} /> : null}>
                                        {!isSubmitting && (contaId ? "Salvar" : "Adicionar")}
                                    </MDButton>
                                </Box>
                            </MDBox>
                        </Form>
                    )}
                </Formik>
            )}
        </Card>
    );
};

ContaBancariaCreateEdit.propTypes = {
    contaId: PropTypes.string,
    closeCreateEdit: PropTypes.func.isRequired,
};

export default ContaBancariaCreateEdit;
